import type { LoggerEvent } from '@/types/logger.types'
import { idleCallbackPromise, timer } from '@/utils/helpers'

import { GoogleFcAdBlockerStatus, type GoogleFC } from './consent-manager'
import { AdConsoleLogger } from './utils/ad-console-logger'

const adConsoleLogger = new AdConsoleLogger('detector')

export type AdBlockStatus = 'enabled' | 'disabled' | 'unknown'

type AdBlockDetector = 'script' | 'fc' | 'both' | 'none'

const FC_TIMEOUT_MS = 5000

function getAdBlockStatusFromFundingChoice(): Promise<AdBlockStatus> {
    return Promise.race([
        new Promise<AdBlockStatus>((resolve) => {
            const fc: GoogleFC = window.googlefc ?? {}
            window.googlefc = fc
            fc.callbackQueue = fc.callbackQueue ?? []
            fc.callbackQueue.push({
                AD_BLOCK_DATA_READY: () => {
                    const status = window.googlefc?.getAdBlockerStatus?.() ?? GoogleFcAdBlockerStatus.UNKNOWN
                    resolve(
                        status === GoogleFcAdBlockerStatus.EXTENSION_LEVEL_AD_BLOCKER ||
                            status === GoogleFcAdBlockerStatus.NETWORK_LEVEL_AD_BLOCKER
                            ? 'enabled'
                            : 'disabled',
                    )
                },
            })
        }),
        timer(FC_TIMEOUT_MS).then<AdBlockStatus>(() => 'unknown'),
    ])
}

async function getAdBlockStatusFromScript(src: string): Promise<AdBlockStatus> {
    await idleCallbackPromise()
    return new Promise((resolve) => {
        const script = document.createElement('script')
        script.type = 'text/javascript'
        script.src = src
        script.async = true
        script.onerror = () => resolve('enabled')
        script.onload = () => resolve('disabled')
        document.head.appendChild(script)
    })
}

export function detectAdBlock(logEvent: (event: LoggerEvent) => void, host?: string): Promise<AdBlockStatus> {
    const fcPromise = getAdBlockStatusFromFundingChoice()
    const scriptPromise = getAdBlockStatusFromScript(`${host || ''}/common/ad.js`)

    Promise.all([fcPromise, scriptPromise]).then(([fcResult, scriptResult]) => {
        const fcDetected = fcResult === 'enabled'
        const scriptDetected = scriptResult === 'enabled'

        let detector: AdBlockDetector
        if (fcDetected && scriptDetected) {
            detector = 'both'
        } else if (fcDetected) {
            detector = 'fc'
        } else if (scriptDetected) {
            detector = 'script'
        } else {
            detector = 'none'
        }

        const fcTimedOut = fcResult === 'unknown'
        adConsoleLogger.debug('adblock', { detector, fcTimedOut })
        logEvent({
            event: 'custom_event',
            eventName: 'ad_blocker_detect_attempt',
            dimension1: fcTimedOut ? 'fc_timeout' : 'fc_no_timeout',
            dimension2: detector,
        })
    })

    return new Promise((resolve) => {
        let fcResult: AdBlockStatus | undefined
        let scriptResult: AdBlockStatus | undefined

        const tryResolve = () => {
            if (fcResult === 'enabled' || scriptResult === 'enabled') {
                resolve('enabled')
                return
            }

            if (fcResult !== undefined && scriptResult !== undefined) {
                if (fcResult === 'disabled' && scriptResult === 'disabled') {
                    resolve('disabled')
                } else {
                    resolve('unknown')
                }
            }
        }

        fcPromise.then((res) => {
            fcResult = res
            tryResolve()
        })

        scriptPromise.then((res) => {
            scriptResult = res
            tryResolve()
        })
    })
}
